<template>
  <div class="vcg-providers">
    <!-- Provider Controls -->
    <div class="provider-controls">
      <div class="controls-wrapper">
        <button 
          class="toggle-all-btn" 
          @click="toggleAll"
          :class="{ 'active': allExpanded }"
        >
          <i :class="allExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          {{ allExpanded ? 'Collapse All Providers' : 'Expand All Providers' }}
        </button>
        
        <div class="provider-count">
          <span class="count-badge">{{ providers.length }}</span> providers found
          <span class="hint-text">Click on the map markers to view provider details</span>
        </div>
      </div>
    </div>

    <!-- Interactive Map -->
    <CommonProviderMap
      v-if="providers && providers.length > 0"
      :providers="commonMapProviders"
      :user-lat="userLat"
      :user-lon="userLon"
      @provider-selected="handleProviderSelected"
    />

    <!-- Provider Cards List -->
    <div class="providers-list">
      <div
        v-for="(provider, index) in providers" 
        :key="index"
        class="provider-card"
        :class="{ 'expanded': collapseStates[index], 'highlighted': isHighlighted(provider) }"
        :id="`provider-card-${index}`"
      >
        <div
          class="provider-card-header"
          @click="toggleCard(index)"
        >
          <div class="provider-card-title">
            <h3 class="provider-name">{{ provider.companyName || 'Unnamed Provider' }}</h3>
            <!-- Business Category Tags wurden entfernt -->
          </div>
          
          <div class="toggle-icon">
            <i :class="collapseStates[index] ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </div>
        </div>

        <div v-if="collapseStates[index]" class="provider-card-content">
          <!-- Provider Details -->
          <div class="provider-details">
            <!-- Description -->
            <div class="detail-section" v-if="provider.description">
              <div class="section-header">
                <i class="bi bi-info-circle"></i>
                <h4 class="section-title">Description</h4>
              </div>
              <p class="description-text">{{ provider.description }}</p>
            </div>
            
            <!-- Business Categories -->
            <div class="detail-section" v-if="hasBusinessCategories(provider)">
              <div class="section-header">
                <i class="bi bi-briefcase"></i>
                <h4 class="section-title">Business Categories</h4>
                <div class="section-hint">
                  These business categories correspond to the sectors in which the provider operates
                </div>
              </div>
              <div class="business-categories">
                <span 
                  v-for="(category, cIdx) in provider.businessCategory" 
                  :key="`category-${cIdx}`"
                  class="business-category-tag"
                >
                  {{ category }}
                </span>
              </div>
            </div>
            
            <!-- Contact Information -->
            <div class="detail-section">
              <div class="section-header">
                <i class="bi bi-person-badge"></i>
                <h4 class="section-title">Contact Information</h4>
              </div>
              <div class="contact-info">
                <div class="contact-item" v-if="provider.contact?.email">
                  <div class="contact-label">Email</div>
                  <div class="contact-value">{{ provider.contact.email }}</div>
                </div>
                
                <div class="contact-item" v-if="provider.contact?.phone">
                  <div class="contact-label">Phone</div>
                  <div class="contact-value">{{ provider.contact.phone }}</div>
                </div>
                
                <div class="contact-item">
                  <div class="contact-label">Location</div>
                  <div class="contact-value">{{ formatAddress(provider.location) || 'Not specified' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import CommonProviderMap from './CommonProviderMap.vue'

export default {
  name: 'VcgProviders',
  components: {
    CommonProviderMap
  },
  props: {
    providers: {
      type: Array,
      default: () => []
    },
    userLat: {
      type: Number,
      default: null
    },
    userLon: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const collapseStates = ref([]);
    const selectedMapProvider = ref(null);
    
    // Initialize collapse states for each provider (all collapsed by default)
    watch(
      () => props.providers,
      (newProviders) => {
        collapseStates.value = newProviders.map(() => false);
      },
      { immediate: true }
    );

    // Toggle collapse state for a specific provider card
    const toggleCard = (index) => {
      collapseStates.value[index] = !collapseStates.value[index];
      
      if (collapseStates.value[index]) {
        // If this card was expanded, update the selected map provider
        selectedMapProvider.value = props.providers[index];
      } else if (selectedMapProvider.value === props.providers[index]) {
        // If this card was collapsed and it was the selected map provider, clear selection
        selectedMapProvider.value = null;
      }
    };

    // Check if all providers are expanded
    const allExpanded = computed(() =>
      collapseStates.value.every((state) => state === true)
    );

    // Toggle all provider cards
    const toggleAll = () => {
      const newState = !allExpanded.value;
      collapseStates.value = collapseStates.value.map(() => newState);
      
      // Clear selected map provider if collapsing all
      if (!newState) {
        selectedMapProvider.value = null;
      }
    };

    // Check if a provider is the currently selected one from the map
    const isHighlighted = (provider) => {
      if (!selectedMapProvider.value) return false;
      return provider.companyName === selectedMapProvider.value.companyName;
    };

    // Handle when a provider is selected from the map
    const handleProviderSelected = (provider) => {
      selectedMapProvider.value = provider;
      
      // Find and expand the corresponding provider card
      const providerIndex = props.providers.findIndex(p => 
        p.companyName === provider.companyName
      );
      
      if (providerIndex !== -1) {
        collapseStates.value[providerIndex] = true;
        
        // We no longer automatically scroll to the provider card
        // This keeps the user's focus on the map
      }
    };

    // Format address from location object
    const formatAddress = (loc) => {
      if (!loc) return '';
      const addr = loc.address || '';
      const city = loc.city || '';
      const postal = loc.postalCode || '';
      const country = loc.country || '';
      let combined = `${addr}, ${postal} ${city}, ${country}`.replace(/\s+,/g, '');
      combined = combined.trim().replace(/\s+/g, ' ');
      return combined || '';
    };

    // Check if provider has business categories
    const hasBusinessCategories = (provider) => {
      return provider && 
             Array.isArray(provider.businessCategory) && 
             provider.businessCategory.length > 0;
    };

    // Transform provider data for the CommonProviderMap
    const commonMapProviders = computed(() => {
      return props.providers.map(p => {
        let lat = null;
        let lon = null;
        // VCG => location.latitude / location.longitude
        if (p.location && typeof p.location.latitude === 'number' && typeof p.location.longitude === 'number') {
          lon = p.location.longitude;
          lat = p.location.latitude;
        }

        let bc = Array.isArray(p.businessCategory)
          ? p.businessCategory.join(', ')
          : (p.businessCategory || '');

        return {
          company_name: p.companyName || '',
          description: p.description || '',
          location_geo: (lat !== null && lon !== null)
            ? { type: 'Point', coordinates: [lon, lat] }
            : null,
          dist_km: null,
          business_category: bc,
          provider_origin: 'vcg.ai', // Geändert von 'foodcycle.ai' zu 'vcg.ai'

          // optional: we copy fields that CommonProviderMap might need
          contact: p.contact || null,
          location: p.location || null,
          businessCategory: p.businessCategory || null,
          companyName: p.companyName || ''  // Keep original property for reference
        };
      });
    });

    return {
      collapseStates,
      selectedMapProvider,
      toggleCard,
      toggleAll,
      allExpanded,
      isHighlighted,
      handleProviderSelected,
      formatAddress,
      hasBusinessCategories,
      commonMapProviders
    };
  }
};
</script>

<style scoped>
.vcg-providers {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Provider Controls */
.provider-controls {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #eee;
}

.controls-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.toggle-all-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f2f5e7;
  border: 1px solid rgba(135, 177, 64, 0.3);
  color: #558000;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-all-btn:hover {
  background-color: rgba(135, 177, 64, 0.2);
}

.toggle-all-btn.active {
  background-color: #87b140;
  color: white;
  border-color: #87b140;
}

.provider-count {
  font-size: 0.95rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.count-badge {
  background-color: #87b140;
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
}

.hint-text {
  font-size: 0.85rem;
  color: #888;
  font-style: italic;
}

/* Providers List */
.providers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.provider-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.3s ease;
}

.provider-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.provider-card.expanded {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.provider-card.highlighted {
  border: 2px solid #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.2), 0 8px 25px rgba(0, 0, 0, 0.1);
}

.provider-card-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.provider-card.expanded .provider-card-header {
  background-color: #f2f5e7;
  border-bottom-color: rgba(135, 177, 64, 0.2);
}

.provider-card.highlighted .provider-card-header {
  background-color: #f2f5e7;
  border-bottom-color: rgba(135, 177, 64, 0.2);
}

.provider-card-header:hover {
  background-color: #f2f5e7;
}

.provider-card-title {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.provider-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.toggle-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #666;
  background-color: rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.provider-card.expanded .toggle-icon,
.provider-card.highlighted .toggle-icon {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
}

/* Provider Card Content */
.provider-card-content {
  padding: 0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.provider-details {
  display: flex;
  flex-direction: column;
}

.detail-section {
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.detail-section:last-child {
  border-bottom: none;
}

.section-header {
  margin-bottom: 1.25rem;
}

.section-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header i {
  color: #87b140;
  font-size: 1.1rem;
}

.section-hint {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

.description-text {
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Business Categories */
.business-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.business-category-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Contact Info */
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.contact-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.contact-value {
  color: #333;
  font-size: 0.95rem;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .provider-card-header {
    padding: 1rem 1.25rem;
  }
  
  .detail-section {
    padding: 1.25rem;
  }
  
  .provider-name {
    font-size: 1.1rem;
  }
  
  .contact-info {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .controls-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .toggle-all-btn {
    width: 100%;
    justify-content: center;
  }
  
  .provider-count {
    justify-content: center;
    text-align: center;
  }
}
</style>